import { html, nothing } from "lit";
import type { LlmStatsTabId } from "../controllers/llm-stats-scope.ts";
import type { NetworkSpendNodeEntry, NetworkSpendResult } from "../types.ts";
import { renderLlmStatsAnalytics, type LlmStatsAnalyticsProps } from "./llm-stats-analytics.ts";
import { renderLlmStatsCapacity, type LlmStatsCapacityProps } from "./llm-stats-capacity.ts";
import { renderLlmStatsScopeBar, type LlmStatsScopeBarProps } from "./llm-stats-scope-bar.ts";
import { renderLlmStatsTabs } from "./llm-stats-tabs.ts";
import { renderTraceView, type TraceViewProps } from "./llm-stats-trace.ts";
import {
  renderUsage,
  type SessionLogEntry,
  type SessionLogRole,
  type UsageColumnId,
  type UsageProps,
} from "./usage.ts";

export type { SessionLogEntry, SessionLogRole, UsageColumnId };

// ─── Sparkline helper ─────────────────────────────────────────────────────────
function renderSparklineSvg(
  values: number[],
  opts?: { color?: string; width?: number; height?: number },
) {
  if (values.length < 2) return nothing;
  const w = opts?.width ?? 80;
  const h = opts?.height ?? 28;
  const color = opts?.color ?? "var(--accent)";
  const max = Math.max(...values, 1);
  const step = w / Math.max(1, values.length - 1);
  const points = values
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  return html`
    <svg
      class="llm-stats-hero-sparkline"
      width=${w}
      height=${h}
      viewBox="0 0 ${w} ${h}"
      aria-hidden="true"
      style="overflow:visible"
    >
      <polyline
        fill="none"
        stroke=${color}
        stroke-width="1.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        points=${points}
        opacity="0.7"
      />
    </svg>
  `;
}

// ─── Trend badge ───────────────────────────────────────────────────────────────
function renderTrendBadge(current: number, previous: number) {
  if (!previous || previous === 0) return nothing;
  const pct = ((current - previous) / previous) * 100;
  const dir = pct >= 0 ? "up" : "down";
  return html`
    <span class="llm-stats-trend-badge ${dir}">
      ${dir === "up" ? "↑" : "↓"} ${Math.abs(pct).toFixed(1)}%
    </span>
  `;
}

// ─── Quick date range chips ────────────────────────────────────────────────────
export type QuickRange = "today" | "7d" | "30d" | "90d" | "month" | "quarter" | "custom";
const QUICK_RANGES: Array<{ id: QuickRange; label: string }> = [
  { id: "today", label: "Today" },
  { id: "7d", label: "Week" },
  { id: "30d", label: "Month" },
  { id: "90d", label: "Quarter" },
  { id: "custom", label: "Custom" },
];

export function quickRangeToDays(r: QuickRange): number | null {
  if (r === "today") return 1;
  if (r === "7d") return 7;
  if (r === "30d") return 30;
  if (r === "90d") return 90;
  if (r === "month") return 30;
  if (r === "quarter") return 90;
  return null; // custom: caller must provide explicit dates
}

export function renderQuickRangeChips(active: QuickRange, onChange: (r: QuickRange) => void) {
  // Map alias presets (month/quarter) back to the underlying chip id so
  // the active highlight stays in sync with the visible label.
  const normalized = active === "month" ? "30d" : active === "quarter" ? "90d" : active;
  return html`
    <div class="llm-stats-quick-range">
      ${QUICK_RANGES.map(
        (r) => html`
          <button
            type="button"
            class="quick-range-chip ${normalized === r.id ? "active" : ""}"
            data-quick-range=${r.id}
            @click=${() => onChange(r.id)}
          >
            ${r.label}
          </button>
        `,
      )}
    </div>
  `;
}

// ─── Token / Cost bar chart ────────────────────────────────────────────────────
type DailyChartPoint = { date: string; tokens: number; cost: number };

function renderDailyCostChart(daily: DailyChartPoint[]) {
  if (daily.length < 1) {
    return html`
      <div class="llm-stats-chart-empty">No daily data yet for the selected range.</div>
    `;
  }
  const width = 720;
  const height = 200;
  const padding = { top: 18, right: 18, bottom: 28, left: 44 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;
  const maxCost = Math.max(...daily.map((d) => d.cost), 1);
  const maxTokens = Math.max(...daily.map((d) => d.tokens), 1);
  const barGap = Math.max(2, Math.min(8, Math.floor(innerW / (daily.length * 4))));
  const barW = Math.max(2, Math.floor((innerW - barGap * (daily.length - 1)) / daily.length));

  const bars = daily.map((d, i) => {
    const x = padding.left + i * (barW + barGap);
    const h = (d.cost / maxCost) * innerH;
    const y = padding.top + (innerH - h);
    return html`
      <g data-date=${d.date}>
        <rect
          x=${x}
          y=${y}
          width=${barW}
          height=${h}
          fill="var(--accent, #ff5c5c)"
          opacity="0.85"
          rx="2"
        >
          <title>${d.date}: ${fmtUsd(d.cost)} · ${fmtNum(d.tokens)} tokens</title>
        </rect>
      </g>
    `;
  });

  const yTicks = 4;
  const tickEls = Array.from({ length: yTicks + 1 }, (_, i) => {
    const ratio = i / yTicks;
    const y = padding.top + innerH - ratio * innerH;
    const label = fmtUsd(maxCost * ratio);
    return html`
      <g>
        <line
          x1=${padding.left}
          y1=${y}
          x2=${padding.left + innerW}
          y2=${y}
          stroke="var(--border, rgba(0,0,0,0.08))"
          stroke-dasharray="2 4"
        ></line>
        <text
          x=${padding.left - 6}
          y=${y + 4}
          text-anchor="end"
          font-size="10"
          fill="var(--muted, #888)"
        >
          ${label}
        </text>
      </g>
    `;
  });

  const xLabels: ReturnType<typeof html>[] = [];
  const labelStep = Math.max(1, Math.ceil(daily.length / 6));
  daily.forEach((d, i) => {
    if (i % labelStep !== 0 && i !== daily.length - 1) return;
    const x = padding.left + i * (barW + barGap) + barW / 2;
    const y = padding.top + innerH + 14;
    const label = d.date.slice(5); // MM-DD
    xLabels.push(
      html`<text
        x=${x}
        y=${y}
        text-anchor="middle"
        font-size="10"
        fill="var(--muted, #888)"
      >
        ${label}
      </text>`,
    );
  });

  const tokenPoints = daily
    .map((d, i) => {
      const x = padding.left + i * (barW + barGap) + barW / 2;
      const y = padding.top + innerH - (d.tokens / maxTokens) * innerH;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  const totals = daily.reduce(
    (acc, d) => ({ tokens: acc.tokens + d.tokens, cost: acc.cost + d.cost }),
    { tokens: 0, cost: 0 },
  );

  return html`
    <div class="llm-stats-chart">
      <div class="llm-stats-chart-header">
        <strong>Daily cost & tokens</strong>
        <span class="muted">
          ${fmtUsd(totals.cost)} · ${fmtK(totals.tokens)}k tokens · ${daily.length} day${daily.length === 1 ? "" : "s"}
        </span>
      </div>
      <svg
        class="llm-stats-chart-svg"
        viewBox="0 0 ${width} ${height}"
        width="100%"
        height=${height}
        role="img"
        aria-label="Daily cost and tokens chart"
      >
        ${tickEls}
        ${bars}
        <polyline
          points=${tokenPoints}
          fill="none"
          stroke="var(--info, #14b8a6)"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          opacity="0.9"
        ></polyline>
        ${xLabels}
      </svg>
      <div class="llm-stats-chart-legend">
        <span><span class="dot" style="background: var(--accent, #ff5c5c)"></span> Cost (USD)</span>
        <span><span class="dot" style="background: var(--info, #14b8a6)"></span> Tokens</span>
      </div>
    </div>
  `;
}

// ─── Per-model breakdown table ─────────────────────────────────────────────────
type ModelBreakdownRow = {
  provider?: string;
  model?: string;
  count: number;
  tokens: number;
  cost: number;
};

export function summarizeModelBreakdown(
  byModel:
    | Array<{
        provider?: string;
        model?: string;
        count: number;
        totals: { totalTokens: number; totalCost: number };
      }>
    | undefined
    | null,
): ModelBreakdownRow[] {
  if (!Array.isArray(byModel)) return [];
  return byModel
    .map((entry) => ({
      provider: entry.provider,
      model: entry.model,
      count: Number(entry.count ?? 0),
      tokens: Number(entry.totals?.totalTokens ?? 0),
      cost: Number(entry.totals?.totalCost ?? 0),
    }))
    .filter((row) => row.model || row.provider)
    .toSorted((a, b) => b.cost - a.cost);
}

function renderModelBreakdown(rows: ModelBreakdownRow[]) {
  if (rows.length === 0) {
    return html`
      <div class="llm-stats-empty" style="padding: 24px 16px">
        <div class="llm-stats-empty-icon">🤖</div>
        <div class="llm-stats-empty-title">No model breakdown yet</div>
        <div class="llm-stats-empty-desc">
          Once sessions are recorded in the selected range, models will appear here.
        </div>
      </div>
    `;
  }
  const totalCost = rows.reduce((acc, r) => acc + r.cost, 0);
  const totalTokens = rows.reduce((acc, r) => acc + r.tokens, 0);
  const maxCost = Math.max(...rows.map((r) => r.cost), 1);
  return html`
    <table class="llm-stats-network-table llm-stats-model-table">
      <thead>
        <tr>
          <th>Model</th>
          <th>Provider</th>
          <th style="text-align: right;">Requests</th>
          <th style="text-align: right;">Tokens</th>
          <th style="text-align: right;">Cost</th>
          <th>Share</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(
          (row) => html`
            <tr>
              <td>
                <div style="font-weight: 600; color: var(--text-strong);">
                  ${row.model ?? "(unknown)"}
                </div>
              </td>
              <td class="llm-stats-mono llm-stats-muted">${row.provider ?? "—"}</td>
              <td class="llm-stats-mono" style="text-align: right;">${fmtNum(row.count)}</td>
              <td class="llm-stats-mono" style="text-align: right;">
                ${fmtK(row.tokens)}<span class="llm-stats-muted">k</span>
              </td>
              <td
                class="llm-stats-mono"
                style="text-align: right; font-weight: 700; color: var(--text-strong);"
              >
                ${fmtUsd(row.cost)}
              </td>
              <td>
                <div
                  class="llm-stats-model-share"
                  style="display: flex; align-items: center; gap: 8px;"
                >
                  <div
                    style="flex: 1; height: 6px; border-radius: 999px; background: var(--bg-elevated); overflow: hidden;"
                  >
                    <div
                      style="width: ${((row.cost / maxCost) * 100).toFixed(1)}%; height: 100%; background: var(--accent, #ff5c5c);"
                    ></div>
                  </div>
                  <span class="llm-stats-mono llm-stats-muted" style="font-size: 11px;">
                    ${totalCost > 0 ? ((row.cost / totalCost) * 100).toFixed(1) : "0.0"}%
                  </span>
                </div>
              </td>
            </tr>
          `,
        )}
        <tr>
          <td colspan="3" style="font-weight: 700; color: var(--text-strong);">Total</td>
          <td
            class="llm-stats-mono"
            style="text-align: right; font-weight: 700;"
          >
            ${fmtK(totalTokens)}<span class="llm-stats-muted">k</span>
          </td>
          <td
            class="llm-stats-mono"
            style="text-align: right; font-weight: 700; color: var(--accent, #ff5c5c);"
          >
            ${fmtUsd(totalCost)}
          </td>
          <td></td>
        </tr>
      </tbody>
    </table>
  `;
}

const fmtUsd = (value: number) =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const fmtNum = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
    Number.isFinite(value) ? value : 0,
  );

const fmtK = (value: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
    Number.isFinite(value) ? value / 1000 : 0,
  );

const fmtRelative = (ms: number | null | undefined) => {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  if (diff < 60000) return "just now";
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return `${Math.floor(diff / 86400000)}d ago`;
};

export type { LlmStatsAnalyticsProps };

export type LlmStatsProps = Omit<
  UsageProps,
  | "onApplyQuery"
  | "selectedHours"
  | "timeSeriesMode"
  | "timeSeriesBreakdownMode"
  | "onSelectHour"
  | "onTimeSeriesModeChange"
  | "onTimeSeriesBreakdownChange"
> & {
  spendResult: NetworkSpendResult | null;
  spendError?: string | null;
  onApplyQuery?: () => void;
  onQuerySubmit?: () => void;
  onQueryChange?: (query: string) => void;
  selectedHours?: UsageProps["selectedHours"];
  timeSeriesMode?: UsageProps["timeSeriesMode"];
  timeSeriesBreakdownMode?: UsageProps["timeSeriesBreakdownMode"];
  onSelectHour?: UsageProps["onSelectHour"];
  onTimeSeriesModeChange?: UsageProps["onTimeSeriesModeChange"];
  onTimeSeriesBreakdownChange?: UsageProps["onTimeSeriesBreakdownChange"];
  capacity?: LlmStatsCapacityProps;
  analytics?: LlmStatsAnalyticsProps | null;
  activeTab: LlmStatsTabId;
  onTabChange: (tab: LlmStatsTabId) => void;
  scopeBar: LlmStatsScopeBarProps;
  // Session trace
  sessionTrace: TraceViewProps["trace"];
  sessionTraceLoading: boolean;
  sessionTraceError: string | null;
  // Quick date range
  quickRange?: QuickRange;
  onQuickRangeChange?: (r: QuickRange) => void;
};

function toUsageProps(params: LlmStatsProps): UsageProps {
  const applyQuery = params.onApplyQuery ?? params.onQuerySubmit ?? (() => {});
  return {
    loading: params.loading,
    error: params.error,
    startDate: params.startDate,
    endDate: params.endDate,
    sessions: params.sessions,
    sessionsLimitReached: params.sessionsLimitReached,
    totals: params.totals,
    aggregates: params.aggregates,
    costDaily: params.costDaily,
    selectedSessions: params.selectedSessions,
    selectedDays: params.selectedDays,
    selectedHours: params.selectedHours ?? [],
    chartMode: params.chartMode,
    dailyChartMode: params.dailyChartMode,
    timeSeriesMode: params.timeSeriesMode ?? "cumulative",
    timeSeriesBreakdownMode: params.timeSeriesBreakdownMode ?? "total",
    timeSeries: params.timeSeries,
    timeSeriesLoading: params.timeSeriesLoading,
    sessionLogs: params.sessionLogs,
    sessionLogsLoading: params.sessionLogsLoading,
    sessionLogsExpanded: params.sessionLogsExpanded,
    logFilterRoles: params.logFilterRoles,
    logFilterTools: params.logFilterTools,
    logFilterHasTools: params.logFilterHasTools,
    logFilterQuery: params.logFilterQuery,
    query: params.query,
    queryDraft: params.queryDraft,
    sessionSort: params.sessionSort,
    sessionSortDir: params.sessionSortDir,
    recentSessions: params.recentSessions,
    sessionsTab: params.sessionsTab,
    visibleColumns: params.visibleColumns,
    timeZone: params.timeZone,
    contextExpanded: params.contextExpanded,
    headerPinned: params.headerPinned,
    onStartDateChange: params.onStartDateChange,
    onEndDateChange: params.onEndDateChange,
    onRefresh: params.onRefresh,
    onTimeZoneChange: params.onTimeZoneChange,
    onToggleContextExpanded: params.onToggleContextExpanded,
    onToggleHeaderPinned: params.onToggleHeaderPinned,
    onToggleSessionLogsExpanded: params.onToggleSessionLogsExpanded,
    onLogFilterRolesChange: params.onLogFilterRolesChange,
    onLogFilterToolsChange: params.onLogFilterToolsChange,
    onLogFilterHasToolsChange: params.onLogFilterHasToolsChange,
    onLogFilterQueryChange: params.onLogFilterQueryChange,
    onLogFilterClear: params.onLogFilterClear,
    onSelectSession: params.onSelectSession,
    onChartModeChange: params.onChartModeChange,
    onDailyChartModeChange: params.onDailyChartModeChange,
    onTimeSeriesModeChange: params.onTimeSeriesModeChange ?? (() => {}),
    onTimeSeriesBreakdownChange: params.onTimeSeriesBreakdownChange ?? (() => {}),
    onSelectDay: params.onSelectDay,
    onSelectHour: params.onSelectHour ?? (() => {}),
    onClearDays: params.onClearDays,
    onClearHours: params.onClearHours,
    onClearSessions: params.onClearSessions,
    onClearFilters: params.onClearFilters,
    onQueryDraftChange: (query) => {
      params.onQueryDraftChange(query);
      params.onQueryChange?.(query);
    },
    onApplyQuery: applyQuery,
    onClearQuery: params.onClearQuery,
    onSessionSortChange: params.onSessionSortChange,
    onSessionSortDirChange: params.onSessionSortDirChange,
    onSessionsTabChange: params.onSessionsTabChange,
    onToggleColumn: params.onToggleColumn,
  };
}

function renderNodeStatus(status: string | undefined) {
  const s = (status ?? "").toLowerCase();
  if (s === "online" || s === "up") {
    return html`
      <span class="llm-stats-pill online">◉ Online</span>
    `;
  }
  if (s === "offline" || s === "down") {
    return html`
      <span class="llm-stats-pill offline">○ Offline</span>
    `;
  }
  return html`<span class="llm-stats-pill unknown">${status ?? "—"}</span>`;
}

function renderNetworkNodeRow(node: NetworkSpendNodeEntry) {
  return html`
    <tr>
      <td>
        <div style="font-weight: 600; color: var(--text-strong);">${node.name}</div>
        <div class="llm-stats-mono llm-stats-muted" style="font-size: 11px; margin-top: 2px;">
          ${node.nodeId}
        </div>
      </td>
      <td>${renderNodeStatus(node.status)}</td>
      <td class="llm-stats-mono" style="font-size: 14px; font-weight: 700; color: var(--text-strong);">
        ${fmtUsd(node.spend.totalCost)}
      </td>
      <td class="llm-stats-mono">
        <span style="font-size: 14px; font-weight: 600;">${fmtK(node.spend.totalTokens)}</span>
        <span class="llm-stats-muted">k</span>
      </td>
      <td class="llm-stats-mono llm-stats-muted">${fmtRelative(node.spend.updatedAt)}</td>
      <td class="llm-stats-mono llm-stats-muted">${fmtRelative(node.lastHeartbeat)}</td>
      <td>
        ${
          node.spend.missing
            ? html`
                <span class="llm-stats-pill warn">⚠ missing</span>
              `
            : nothing
        }
      </td>
    </tr>
  `;
}

function renderFleetSummary(params: LlmStatsProps) {
  const sessionCost = params.totals?.totalCost ?? 0;
  const sessionTokens = params.totals?.totalTokens ?? 0;
  const networkTotals = params.spendResult?.totals ?? null;
  const networkCost = networkTotals?.totalCost ?? 0;
  const networkTokens = networkTotals?.totalTokens ?? 0;
  const nodesReporting = (networkTotals?.nodes ?? 0) - (networkTotals?.nodesMissing ?? 0);
  const nodeCount = networkTotals?.nodes ?? 0;
  const totalCost = sessionCost + networkCost;
  const totalTokens = sessionTokens + networkTokens;
  const dailyData = (params.costDaily ?? []).map((d) => ({
    date: d.date,
    tokens: d.totalTokens,
    cost: d.totalCost,
  }));
  const modelRows = summarizeModelBreakdown(params.aggregates?.byModel);

  return html`
    <section class="llm-stats-section">
      <div class="llm-stats-section-header">
        <div class="llm-stats-section-title">
          <span class="llm-stats-section-title-icon">◎</span>
          Fleet LLM Spend
        </div>
        <span class="llm-stats-mono llm-stats-muted" style="font-size: 11px;">
          Updated ${fmtRelative(params.spendResult?.updatedAt ?? null)}
        </span>
      </div>
      <div class="llm-stats-section-body">
        <!-- KPI cards -->
        <div class="llm-stats-kpi-grid">
          <div class="llm-stats-kpi-card">
            <div class="kpi-label">Session LLM Cost</div>
            <div class="kpi-value accent">${fmtUsd(sessionCost)}</div>
            <div class="kpi-sub">
              <strong>${fmtNum(sessionTokens)}</strong> tokens in range
            </div>
          </div>
          <div class="llm-stats-kpi-card">
            <div class="kpi-label">Network LLM Cost</div>
            <div class="kpi-value">${fmtUsd(networkCost)}</div>
            <div class="kpi-sub">
              <strong>${fmtNum(networkTokens)}</strong> tokens across fleet
            </div>
          </div>
          <div class="llm-stats-kpi-card">
            <div class="kpi-label">Combined LLM Cost</div>
            <div class="kpi-value accent">${fmtUsd(totalCost)}</div>
            <div class="kpi-sub">
              <strong>${fmtNum(totalTokens)}</strong> total tokens
            </div>
          </div>
          <div class="llm-stats-kpi-card">
            <div class="kpi-label">Nodes Reporting</div>
            <div class="kpi-value">${fmtNum(nodesReporting)}<span style="font-size:16px;color:var(--muted)">/${fmtNum(nodeCount)}</span></div>
            <div class="kpi-sub">
              ${
                nodeCount > 0
                  ? html`<strong>${Math.round((nodesReporting / nodeCount) * 100)}%</strong> fleet healthy`
                  : html`
                      No nodes reporting
                    `
              }
            </div>
          </div>
        </div>

        ${
          params.spendError
            ? html`<div class="llm-stats-error">⚠ ${params.spendError}</div>`
            : nothing
        }

        ${renderDailyCostChart(dailyData)}

        <div class="llm-stats-section-header" style="margin-top: 16px; padding: 12px 0;">
          <div class="llm-stats-section-title">
            <span class="llm-stats-section-title-icon">🤖</span>
            Per-model breakdown
          </div>
          <span class="llm-stats-mono llm-stats-muted" style="font-size: 11px;">
            ${modelRows.length} model${modelRows.length === 1 ? "" : "s"}
          </span>
        </div>
        ${renderModelBreakdown(modelRows)}

        <div class="llm-stats-section-header" style="margin-top: 16px; padding: 12px 0;">
          <div class="llm-stats-section-title">
            <span class="llm-stats-section-title-icon">▣</span>
            Per-node spend
          </div>
        </div>
        <table class="llm-stats-network-table">
          <thead>
            <tr>
              <th>Node</th>
              <th>Status</th>
              <th>Cost</th>
              <th>Tokens</th>
              <th>Spend updated</th>
              <th>Last heartbeat</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            ${(params.spendResult?.nodes ?? []).map((node) => renderNetworkNodeRow(node))}
            ${
              !(params.spendResult?.nodes?.length ?? 0)
                ? html`
                    <tr>
                      <td colspan="7">
                        <div class="llm-stats-empty">
                          <div class="llm-stats-empty-icon">◎</div>
                          <div class="llm-stats-empty-title">No network nodes found</div>
                          <div class="llm-stats-empty-desc">
                            No fleet nodes are reporting spend data for the selected date range.
                          </div>
                        </div>
                      </td>
                    </tr>
                  `
                : nothing
            }
          </tbody>
        </table>
      </div>
    </section>
  `;
}

function renderHeroSection(params: LlmStatsProps) {
  const sessionCost = params.totals?.totalCost ?? 0;
  const sessionTokens = params.totals?.totalTokens ?? 0;
  const networkTotals = params.spendResult?.totals ?? null;
  const networkCost = networkTotals?.totalCost ?? 0;
  const networkTokens = networkTotals?.totalTokens ?? 0;
  const nodesReporting = (networkTotals?.nodes ?? 0) - (networkTotals?.nodesMissing ?? 0);
  const totalCost = sessionCost + networkCost;
  const totalTokens = sessionTokens + networkTokens;

  // Build sparkline data from costDaily (last 7 days of trend)
  const dailyData = params.costDaily ?? [];
  const sparklineCost = dailyData.slice(-7).map((d) => d.totalCost);

  return html`
    <div class="llm-stats-hero">
      ${
        params.onQuickRangeChange
          ? renderQuickRangeChips(params.quickRange ?? "7d", params.onQuickRangeChange)
          : nothing
      }
      <div class="llm-stats-hero-row">
        <div class="llm-stats-hero-kpi">
          <div class="kpi-label">Total LLM Cost</div>
          <div class="kpi-value accent">${fmtUsd(totalCost)}</div>
          ${sparklineCost.length > 1 ? renderSparklineSvg(sparklineCost, { color: "var(--accent)" }) : nothing}
          <div class="kpi-sub">
            <strong>${fmtNum(totalTokens)}</strong> tokens consumed
          </div>
        </div>
        <div class="llm-stats-hero-kpi">
          <div class="kpi-label">Session Cost</div>
          <div class="kpi-value">${fmtUsd(sessionCost)}</div>
          ${
            sparklineCost.length > 1
              ? renderSparklineSvg(
                  sparklineCost.map((c) => (c / Math.max(totalCost, 1)) * sessionCost),
                  { color: "var(--ok)" },
                )
              : nothing
          }
          <div class="kpi-sub">
            <strong>${fmtNum(sessionTokens)}</strong> tokens · ${params.sessions?.length ?? 0} sessions
          </div>
        </div>
        <div class="llm-stats-hero-kpi">
          <div class="kpi-label">Network Cost</div>
          <div class="kpi-value">${fmtUsd(networkCost)}</div>
          ${
            sparklineCost.length > 1
              ? renderSparklineSvg(
                  sparklineCost.map((c) => (c / Math.max(totalCost, 1)) * networkCost),
                  { color: "var(--info)" },
                )
              : nothing
          }
          <div class="kpi-sub">
            <strong>${fmtNum(networkTokens)}</strong> tokens · <strong>${fmtNum(nodesReporting)}</strong> nodes
          </div>
        </div>
        <div class="llm-stats-hero-kpi">
          <div class="kpi-label">Fleet Status</div>
          <div class="kpi-value">${fmtNum(nodesReporting)}<span style="font-size:18px;color:var(--muted)">/${fmtNum(networkTotals?.nodes ?? 0)}</span></div>
          <div class="kpi-sub">
            nodes reporting · <strong>${fmtNum(params.sessions?.length ?? 0)}</strong> sessions in range
          </div>
        </div>
      </div>
    </div>
  `;
}

export function renderLlmStats(params: LlmStatsProps) {
  const usageProps = toUsageProps(params);

  return html`
    <div class="llm-stats-page">
      ${renderLlmStatsTabs({
        activeTab: params.activeTab,
        onTabChange: params.onTabChange,
      })}

      ${renderLlmStatsScopeBar(params.scopeBar)}

      ${
        params.activeTab === "overview"
          ? html`
              ${renderHeroSection(params)}
              ${
                params.capacity
                  ? renderLlmStatsCapacity(params.capacity)
                  : html`
                      <section class="llm-stats-section">
                        <div class="llm-stats-section-body">
                          <div class="llm-stats-empty">
                            <div class="llm-stats-empty-icon">◈</div>
                            <div class="llm-stats-empty-title">Local capacity</div>
                            <div class="llm-stats-empty-desc">
                              Connect to the gateway to load host and Ollama headroom.
                            </div>
                          </div>
                        </div>
                      </section>
                    `
              }
            `
          : nothing
      }

      ${params.activeTab === "fleet" ? renderFleetSummary(params) : nothing}

      ${
        params.activeTab === "analytics"
          ? html`
              <section class="card llm-stats-analytics-section">
                <div class="llm-stats-section-title">
                  <div class="llm-stats-section-icon">📊</div>
                  Analytics
                </div>
                ${
                  params.analytics
                    ? renderLlmStatsAnalytics(params.analytics)
                    : html`
                        <div class="llm-stats-empty">
                          <div class="llm-stats-empty-icon">📊</div>
                          <div class="llm-stats-empty-title">No analytics data</div>
                          <div class="llm-stats-empty-desc">
                            Sessions data will appear here once the overview tab loads usage.
                          </div>
                        </div>
                      `
                }
              </section>
            `
          : nothing
      }

      ${
        params.activeTab === "trace"
          ? html`
              <section class="card llm-stats-trace-section">
                <div class="llm-stats-section-title">
                  <div class="llm-stats-section-icon">◈</div>
                  Agent Cycle Trace
                </div>
                ${
                  params.sessionTraceLoading
                    ? html`
                        <div class="llm-stats-loading">
                          <div class="llm-stats-spinner"></div>
                          Loading trace…
                        </div>
                      `
                    : params.sessionTraceError
                      ? html`
                          <div class="llm-stats-callout danger">
                            <span class="llm-stats-callout-icon">⚠</span>
                            <div>
                              <div class="llm-stats-callout-text">Trace load failed</div>
                              <div class="llm-stats-callout-sub">${params.sessionTraceError}</div>
                            </div>
                          </div>
                        `
                      : params.sessionTrace
                        ? renderTraceView({
                            trace: params.sessionTrace,
                            loading: false,
                            error: null,
                          })
                        : html`
                            <div class="llm-stats-empty">
                              <div class="llm-stats-empty-icon">◈</div>
                              <div class="llm-stats-empty-title">No trace data</div>
                              <div class="llm-stats-empty-desc">
                                Select a session in the Sessions tab to load its agent cycle trace.
                              </div>
                            </div>
                          `
                }
              </section>
            `
          : nothing
      }

      ${params.activeTab === "sessions" ? renderUsage(usageProps) : nothing}
    </div>
  `;
}
